import { useRef, useEffect, useCallback } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { keyframes } from '@mui/material/styles';

import {
  useDriveChat,
  useAutoScroll,
  useConversationIdParam,
  useFetchConversation,
  useSetReaction,
} from './hooks';
import { EmptyConversationPlaceholder } from './components/empty-conversation-placeholder.component';
import { WelcomeScreen } from './components/welcome-screen.component';
import { ChatMessagesList } from './components/chat-messages-list.component';

import { DriveBackground } from '../components/drive-background.component';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const rootSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const fadeInSx: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  animation: `${fadeIn} 0.4s ease`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
};

const centeredSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
  animation: `${fadeIn} 0.4s ease`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
};

export const DriveInsightsPage = () => {
  const [conversationId, setConversationId] = useConversationIdParam();
  const {
    messages: fetchedMessages,
    loading: fetchingConversation,
    error: fetchError,
  } = useFetchConversation(conversationId);

  const {
    messages,
    submitPrompt,
    formRef,
    setMessages,
    loadMessages,
    isStreaming,
    processingMessage,
  } = useDriveChat({
    conversationId,
    onConversationCreated: setConversationId,
  });

  const loadedConversationIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Navigated away from a conversation (e.g. back to welcome)
    if (!conversationId) {
      if (loadedConversationIdRef.current !== undefined) {
        loadMessages([]);
        loadedConversationIdRef.current = undefined;
      }
      return;
    }

    const isNewConversation = conversationId !== loadedConversationIdRef.current;
    if (!isNewConversation) return;

    // Switching between conversations — clear stale messages to show spinner
    if (fetchingConversation && loadedConversationIdRef.current !== undefined) {
      loadMessages([]);
      return;
    }

    // Fetch complete — load the new conversation's messages.
    // Skip if messages already exist (e.g. from an active submit/stream flow)
    // to avoid loadMessages → stopStream killing the in-flight subscription.
    if (!fetchingConversation) {
      if (messages.length === 0) {
        loadMessages(fetchedMessages);
      }
      loadedConversationIdRef.current = conversationId;
    }
  }, [conversationId, fetchingConversation, fetchedMessages, loadMessages, messages.length]);

  const handleFormSubmit = useCallback(
    (values: { question: string }) => {
      if (isStreaming) return;
      void submitPrompt(values.question);
    },
    [submitPrompt, isStreaming],
  );

  const handleRetry = useCallback(() => {
    const lastPrompt = [...messages].reverse().find((m) => m.type === 'prompt');
    if (lastPrompt) {
      void submitPrompt(lastPrompt.content);
    }
  }, [messages, submitPrompt]);

  const handlePromptClick = useCallback(
    (prompt: string) => {
      formRef.current?.change('question', prompt);
    },
    [formRef],
  );

  const { handleReactionChange } = useSetReaction({ setMessages });
  const scrollRef = useAutoScroll(messages);

  const hasMessages = messages.length > 0;
  const isLoadingConversation =
    !hasMessages && !!conversationId && conversationId !== loadedConversationIdRef.current;
  const isEmptyConversation =
    !hasMessages &&
    !!conversationId &&
    !fetchingConversation &&
    conversationId === loadedConversationIdRef.current &&
    fetchedMessages.length === 0;

  let content;

  if (fetchError) {
    content = (
      <Box key='error' sx={centeredSx}>
        <Alert severity='error'>Failed to load conversation. Please try again later.</Alert>
      </Box>
    );
  } else if (isLoadingConversation) {
    content = (
      <Box key='loading' sx={centeredSx}>
        <CircularProgress />
      </Box>
    );
  } else if (isEmptyConversation) {
    content = <EmptyConversationPlaceholder />;
  } else if (!hasMessages) {
    content = (
      <Box key='welcome' sx={fadeInSx}>
        <DriveBackground />
        <WelcomeScreen
          onPromptClick={handlePromptClick}
          onSubmit={handleFormSubmit}
          formRef={formRef}
          isStreaming={isStreaming}
        />
      </Box>
    );
  } else {
    content = (
      <Box key='conversation' sx={fadeInSx}>
        <ChatMessagesList
          ref={scrollRef}
          messages={messages}
          onSubmit={handleFormSubmit}
          formRef={formRef}
          onReactionChange={handleReactionChange}
          isStreaming={isStreaming}
          processingMessage={processingMessage}
          onRetry={handleRetry}
        />
      </Box>
    );
  }

  return <Box sx={rootSx}>{content}</Box>;
};
